import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useCompany } from '@/context/CompanyContext';
import { secureLogger } from '@/utils/secureLogger';

export interface Sale {
  id: string;
  company_id: string;
  user_id: string;
  product_id: string;
  product_name: string;
  quantity_sold: number;
  unit_price: number;
  total_amount: number;
  sale_date: string;
  created_at: string;
  payment_method: 'Espèces' | 'Carte Bancaire' | 'Chèque' | 'Autre';
  user?: {
    full_name: string | null;
  };
  seller_name?: string;
}

export interface GroupedSale {
  order_number: number;
  sale_datetime: string;
  total_amount: number;
  items_count: number;
  seller_name: string;
  payment_method: 'Espèces' | 'Carte Bancaire' | 'Chèque' | 'Autre';
  items: Sale[];
}

export const useSales = () => {
  const [sales, setSales] = useState<Sale[]>([]);
  const [groupedSales, setGroupedSales] = useState<GroupedSale[]>([]);
  const [loading, setLoading] = useState(true);
  const { currentCompany } = useCompany();

  // Fonction pour regrouper les ventes par date/heure
  const groupSalesByDateTime = (salesData: Sale[]): GroupedSale[] => {
    const groups: { [key: string]: Sale[] } = {};
    
    salesData.forEach(sale => {
      // Regrouper par date/heure (minute) et vendeur
      const date = new Date(sale.sale_date);
      const key = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}-${date.getHours()}-${date.getMinutes()}-${sale.user_id}`;
      
      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(sale);
    });

    // Convertir en tableau avec numéros de commande
    return Object.entries(groups)
      .map(([key, items], index) => {
        const firstItem = items[0];
        return {
          order_number: index + 1,
          sale_datetime: firstItem.sale_date,
          total_amount: items.reduce((sum, item) => sum + item.total_amount, 0),
          items_count: items.reduce((sum, item) => sum + item.quantity_sold, 0),
          seller_name: firstItem.seller_name || 'Utilisateur inconnu',
          payment_method: firstItem.payment_method,
          items: items.sort((a, b) => a.product_name.localeCompare(b.product_name))
        };
      })
      .sort((a, b) => new Date(b.sale_datetime).getTime() - new Date(a.sale_datetime).getTime());
  };

  const fetchSales = async () => {
    if (!currentCompany) return;
    
    try {
      const { data: salesData, error } = await supabase
        .from('sales')
        .select('*')
        .eq('company_id', currentCompany.id)
        .order('sale_date', { ascending: false })
        .limit(100);

      if (error) throw error;

      // Récupérer les profils des utilisateurs ayant effectué ces ventes (sans emails pour la sécurité)
      const userIds = Array.from(new Set((salesData || []).map((s: any) => s.user_id).filter(Boolean)));
      let profilesMap: Record<string, { full_name: string | null }> = {};

      if (userIds.length > 0 && currentCompany.id) {
        try {
          const { data: profilesData } = await supabase
            .rpc('get_sales_user_profiles', { 
              p_company_id: currentCompany.id, 
              p_user_ids: userIds 
            });

          profilesMap = (profilesData || []).reduce((acc: any, p: any) => {
            acc[p.user_id] = { full_name: p.full_name };
            return acc;
          }, {} as Record<string, { full_name: string | null }>);
        } catch (e) {
          console.warn('Impossible de charger certains profils (RLS) :', e);
        }
      }

      const formattedSales = (salesData || []).map((sale: any) => ({
        id: sale.id,
        company_id: sale.company_id,
        user_id: sale.user_id,
        product_id: sale.product_id,
        product_name: sale.product_name,
        quantity_sold: sale.quantity_sold,
        unit_price: sale.unit_price,
        total_amount: sale.total_amount,
        sale_date: sale.sale_date,
        created_at: sale.created_at,
        payment_method: sale.payment_method,
        seller_name: profilesMap[sale.user_id]?.full_name || 'Utilisateur inconnu'
      }));

      secureLogger.info('useSales', 'Setting sales data', { salesCount: formattedSales.length });
      setSales(formattedSales);
      
      // Regrouper les ventes par date/heure (même minute)
      const grouped = groupSalesByDateTime(formattedSales);
      secureLogger.info('useSales', 'Setting grouped sales', { groupCount: grouped.length });
      setGroupedSales(grouped);
    } catch (error) {
      console.error('Erreur lors de la récupération des ventes:', error);
    } finally {
      setLoading(false);
    }
  };

  const processSale = async (
    productId: string,
    quantity: number,
    unitPrice: number,
    paymentMethod: 'Espèces' | 'Carte Bancaire' | 'Chèque' | 'Autre' = 'Espèces'
  ): Promise<boolean> => {
    if (!currentCompany) return false;

    try {
      const { error } = await supabase.rpc('process_sale', {
        p_company_id: currentCompany.id,
        p_product_id: productId,
        p_quantity: quantity,
        p_unit_price: unitPrice,
        p_payment_method: paymentMethod
      });

      if (error) throw error;
      
      // Actualiser les ventes après ajout
      await fetchSales();
      return true;
    } catch (error) {
      console.error('Erreur lors du traitement de la vente:', error);
      return false;
    }
  };

  useEffect(() => {
    fetchSales();
  }, [currentCompany]);

  return {
    sales,
    groupedSales,
    loading,
    processSale,
    refetch: fetchSales
  };
};